import type {
  DashboardExperienceData,
  DashboardExperienceResponse,
  LandingExperienceData,
  LandingExperienceResponse,
  OrderConfirmationBanner,
  SubscriptionSettingsOverviewData,
  DeliveryDayOption,
  SubscriptionSettingsResubscribeData,
  ResubscriptionModalData,
} from './interfaces'

export const LANDING_EXPERIENCE_FIXTURE: LandingExperienceData = {
  heading: 'Use Gousto your way',
  hero: {
    welcome: 'Welcome back, Customer',
    welcomeMobile: 'Welcome back!',
    headline: 'Good food, your way',
    subtitle:
      'Pick the rhythm that suits you.\nA one-off box when you fancy it, or regular deliveries you can skip anytime.',
  },
  banner: {
    title: 'Get 60% off your next box',
    subtitle: 'Applies to either option below',
    expiry_date: '2026-01-05',
    expiry_label: 'Offer ends 5th January 2026',
  },
  options: [
    {
      id: 'one_off_box',
      title: 'Order a one-off box',
      summary: 'Subscription-free flexible ordering',
      tags: ['No commitment', 'Next day delivery available'],
      action: {
        intent: 'start_one_off_order',
        title: 'Order a box',
        analytics: {
          category: 'funnel',
          action: 'order_one_off_box',
          properties: { discount_offered_pct: 60, source: 'inactive_interstitial' },
        },
      },
    },
    {
      id: 'resubscribe',
      title: 'Resubscribe',
      summary: 'For guaranteed regular deliveries',
      tags: ['Best value - Save £2.50 on every box', 'Skip anytime', 'Delivery slot guaranteed'],
      tag_styles: [{ index: 0, highlighted: true, strong_text: ['Best value'] }],
      action: {
        intent: 'reactivate_subscription',
        title: 'Resubscribe',
        analytics: {
          category: 'funnel',
          action: 'resubscribe',
          properties: { discount_offered_pct: 60, source: 'inactive_interstitial' },
        },
      },
    },
  ],
  defer: {
    intent: 'defer_decision',
    title: "I'll decide later",
    analytics: {
      category: 'funnel',
      action: 'decide_later',
      properties: { source: 'inactive_interstitial' },
    },
  },
}

export const LANDING_EXPERIENCE_200_FIXTURE: LandingExperienceResponse = {
  status: 200,
  data: LANDING_EXPERIENCE_FIXTURE,
}

export const LANDING_EXPERIENCE_204_FIXTURE: LandingExperienceResponse = {
  status: 204,
  data: null,
}

export const DASHBOARD_EXPERIENCE_UNSUBSCRIBED_FIXTURE: DashboardExperienceData = {
  sections: [
    {
      type: 'unsubscribed_hero',
      data: {
        welcome: 'Welcome back, Bryce!',
        headline: 'ORDER GOUSTO ON YOUR TERMS',
        offer: {
          badge: '20% OFF',
          label: 'your next box',
          expiry_label: 'Offer ends 03/07/26',
        },
        options: [
          {
            title: 'Order a one-off box',
            summary: 'Subscription-free, flexible ordering. Next day delivery available.',
            icon: 'box',
            action: {
              intent: 'start_one_off_order',
              title: 'Order a one-off box',
              analytics: {
                category: 'funnel',
                action: 'order_one_off_box',
                properties: {
                  discount_offered_pct: 60,
                  source: 'inactive_homepage',
                  transactional_order_origin: 'value_prop_card',
                  button_location: 'value_prop_card',
                  home_page_state: 'no_order',
                },
              },
            },
          },
          {
            title: 'Resubscribe',
            summary: 'For guaranteed regular deliveries. Skip or cancel anytime.',
            icon: 'refresh',
            badge: 'Best value',
            action: {
              intent: 'reactivate_subscription',
              title: 'Resubscribe',
              analytics: {
                category: 'funnel',
                action: 'resubscribe',
                properties: {
                  discount_offered_pct: 60,
                  source: 'inactive_homepage',
                  resurrection_resubscription_origin: 'value_prop_card',
                  home_page_state: 'no_order',
                },
              },
            },
          },
        ],
      },
    },
    {
      type: 'unsubscribed_hero_v2',
      data: {
        welcome: 'Welcome back, Bryce!',
        headline: "Let's get cooking",
        tags: [
          'Choose from 175+ recipes',
          'No subscription, no commitment',
          'Next day delivery available',
        ],
        offer: {
          badge: '40% OFF',
          label: 'your next box',
          expiry_label: 'Offer ends 03/07/26',
        },
        cta: {
          title: 'Browse the new menu',
          intent: 'start_one_off_order',
          analytics: {
            category: 'funnel',
            action: 'browse_new_menu',
            properties: { home_page_state: 'no_order' },
          },
        },
      },
    },
    {
      type: 'heat_and_eat_carousel',
      data: {
        title: 'Need a night off cooking?',
        subtitle:
          'Add a chef-designed ready meal. Real ingredients, no nasties, ready in just 7 mins.',
        tag: 'Heat & Eat',
        meals: [
          {
            id: '54aa761f-0d78-40fc-9928-3b643feb8f02',
            title: 'Mediterranean-Style Chicken Pasta',
            imageUrl:
              'https://production-media.gousto.co.uk/cms/mood-image/mediterranean-x1500.jpg',
            imageAlt: 'Mediterranean-Style Chicken Pasta',
            cookTimeMins: 7,
            kcal: 492,
            analytics: {
              category: 'funnel',
              action: 'click_heat_and_eat_recipe',
              properties: { recipe_id: '54aa761f-0d78-40fc-9928-3b643feb8f02' },
            },
          },
        ],
        action: {
          title: 'Order a heat and eat meal',
          intent: 'view_collection',
          collectionSlug: 'heat-and-eat',
          analytics: {
            category: 'funnel',
            action: 'click_heat_and_eat_upsell',
            properties: {},
          },
        },
        displayAnalytics: {
          category: 'funnel',
          action: 'heat_and_eat_upsell_displayed',
          properties: { home_page_state: 'no_order', add_to_box_cta_shown: false },
        },
      },
    },
    {
      type: 'recipe_carousel',
      data: {
        title: 'Recommendations',
        subtitle: 'Recipes you’ve previously loved or highly rated',
        recipes: [
          {
            id: 'aa897613-f7f7-42e6-82cf-88acbebc4600',
            title: 'Super Green Chicken & Spinach Orzo',
            imageUrl: 'https://production-media.gousto.co.uk/cms/mood-image/example-1-x1500.jpg',
            imageAlt: 'Super Green Chicken & Spinach Orzo',
            cookTimeMins: 30,
            kcal: 561,
          },
        ],
        action: {
          title: 'View all',
          intent: 'view_collection',
          collectionSlug: 'recommendations',
          analytics: {
            category: 'funnel',
            action: 'recipe_view_all',
            properties: {
              row_name: 'new_and_limited',
              collection_id: '56b88b66-7e0b-11ec-a129-02e1cea636b3',
              collection_slug: 'recommendations',
            },
          },
        },
      },
    },
    {
      type: 'dashboard_action_compact',
      data: { title: 'View past deliveries', intent: 'past_deliveries' },
    },
  ],
}

export const DASHBOARD_EXPERIENCE_WITH_ORDER_FIXTURE: DashboardExperienceData = {
  sections: [
    {
      type: 'page_header',
      data: {
        title: 'Welcome back, Bryce',
        subtitle: { text: 'Recipes chosen, edit until **3 Jun**', type: 'positive' },
      },
    },
    {
      type: 'order',
      data: {
        id: '172920098',
        heading: 'UPCOMING DELIVERIES',
        title: 'Saturday 6th June',
        state_title: 'Order',
        state_type: 'neutral',
        summary_text: '3 recipes, 0 items',
        edit_until_label: 'Edit until 3 Jun',
        recipes: [
          {
            id: '2af50c1d-3bd8-4ffa-b074-eaa0b3d1b3b7',
            image_url:
              'https://production-media.gousto.co.uk/cms/mood-image/example-jerk-x1500.jpg',
            accessibility_label: 'Jerk Chicken Roast With Garlic Greens & Yorkshires',
          },
        ],
        price: { kind: 'discounted', amount: '£0.00', original: '£38.76' },
        primary_action: {
          intent: 'edit_recipes',
          title: 'Edit recipes',
          analytics: {
            category: 'funnel',
            action: 'click_edit_recipes',
            properties: {
              order_id: '172920098',
              period_id: '647',
              portion_size: '2',
              delivery_date: '2026-06-06',
              order_state: 'pending',
            },
          },
        },
        help_cta: null,
        details: {
          heading: 'Delivery details',
          delivery_date_label: 'Saturday 6th June',
          delivery_time_label: '08:00 - 19:00',
          delivery_address_title: 'Delivery address',
          delivery_address_label: 'Gousto Shepherds Building, London, W14 0EH',
          selected_day_id: '4475',
          selected_slot_id: '6',
          available_delivery_days: [
            {
              id: '4475',
              formatted_date: 'Saturday 6th June',
              slots: [
                {
                  id: '6',
                  day_slot_lead_time_id: '5f2c4eaa-8218-4230-8e0b-7f12007149af',
                  time_label: '08:00 - 19:00',
                },
              ],
            },
          ],
          edit_slot_action: {
            title: 'Edit',
            error_message: 'There was an error updating your delivery slot. Please try again.',
            analytics: {
              category: 'funnel',
              action: 'OrderDeliverySlot Saved',
              properties: { order_id: '172920098', original_deliveryslot_id: '6' },
            },
          },
          skip_action: {
            title: 'Skip delivery',
            error_message: 'There was an error skipping your delivery. Please try again.',
            analytics: {
              category: 'funnel',
              action: 'cancel_order_click',
              properties: {
                order_id: '172920098',
                period_id: '647',
                portion_size: '2',
                delivery_date: '2026-06-06',
                order_state: 'pending',
              },
            },
          },
        },
        analytics: {
          order_id: '172920098',
          order_state: 'pending',
          order_phase: 'open',
          delivery_day_id: '4475',
          delivery_slot_id: '6',
          portion_size: '2',
          period_id: '647',
          delivery_date: '2026-06-06',
          cut_off_date: '2026-06-03',
        },
      },
    },
    {
      type: 'order_box',
      data: {
        title: 'Order another box',
        tags: ['Choose from 175+ recipes', 'No subscription'],
        cta: {
          title: 'Choose your recipes',
          intent: 'start_one_off_order',
          analytics: {
            category: 'funnel',
            action: 'choose_your_recipes_cta',
            properties: { source: 'inactive_homepage', home_page_state: 'pending_order' },
          },
        },
      },
    },
    {
      type: 'heat_and_eat_carousel',
      data: {
        title: 'Need a night off cooking?',
        subtitle:
          'Add a chef-designed ready meal. Real ingredients, no nasties, ready in just 7 mins.',
        tag: 'Heat & Eat',
        meals: [
          {
            id: '49240dfb-11fa-4160-8927-5e509d360953',
            title: 'Thai Red Chicken Curry With Jasmine Rice',
            imageUrl: 'https://production-media.gousto.co.uk/cms/mood-image/thai-red-x1500.jpg',
            imageAlt: 'Thai Red Chicken Curry With Jasmine Rice',
            cookTimeMins: 7,
            kcal: 421,
            analytics: {
              category: 'funnel',
              action: 'click_heat_and_eat_recipe',
              properties: { recipe_id: '49240dfb-11fa-4160-8927-5e509d360953' },
            },
          },
        ],
        action: {
          title: 'Order a heat and eat meal',
          intent: 'view_collection',
          collectionSlug: 'heat-and-eat',
          analytics: {
            category: 'funnel',
            action: 'click_heat_and_eat_upsell',
            properties: {},
          },
        },
        displayAnalytics: {
          category: 'funnel',
          action: 'heat_and_eat_upsell_displayed',
          properties: { home_page_state: 'pending_order', add_to_box_cta_shown: true },
        },
        secondaryAction: {
          title: 'Add to my 6th Jun box',
          intent: 'add_to_order',
          collectionSlug: 'heat-and-eat',
          orderId: '172920098',
          analytics: {
            category: 'funnel',
            action: 'add_heat_and_eat_to_next_box',
            properties: { order_id: '172920098' },
          },
        },
      },
    },
    {
      type: 'dashboard_action',
      data: {
        title: 'Fancy a regular delivery?',
        subtitle: 'Subscribe for guaranteed regular deliveries. Skip or cancel anytime.',
        icon: 'resub',
        intent: 'reactivate_subscription',
        analytics: {
          category: 'funnel',
          action: 'resubscribe',
          properties: {
            source: 'inactive_homepage',
            resurrection_resubscription_origin: 'value_prop_card',
            home_page_state: 'pending_order',
          },
        },
      },
    },
  ],
}

export const DASHBOARD_EXPERIENCE_200_FIXTURE: DashboardExperienceResponse = {
  status: 200,
  data: DASHBOARD_EXPERIENCE_WITH_ORDER_FIXTURE,
}

export const DASHBOARD_EXPERIENCE_204_FIXTURE: DashboardExperienceResponse = {
  status: 204,
  data: null,
}

export const ORDER_CONFIRMATION_RAF_STEPPER_FIXTURE: OrderConfirmationBanner = {
  type: 'raf_stepper',
  data: {
    title: "Know someone who'd love these recipes?",
    description: 'Earn £20 credit for each friend you bring to Gousto',
    creditFormatted: '£20',
    steps: [
      { label: 'Sign up', status: 'completed' },
      { label: 'Refer a friend', status: 'completed' },
      { label: '£20 credit', status: 'current' },
    ],
    footerText: 'They get 65% off first, then 45% and 25% for 2 months.',
    analytics: {
      category: 'funnel',
      action: 'order_confirmation_raf_banner_displayed',
      properties: {
        banner_variant: 'stepper',
        order_confirmation_type: 'signup',
        credit_formatted: '£20',
        tiers_claimed: 1,
        tiers_total: 3,
      },
    },
    action: {
      intent: 'refer_a_friend',
      title: 'Share my code',
      webTitle: 'Copy share link',
      successMessage: {
        title: 'Link copied, now send it to a friend',
        subtitle: "You'll get £20 credit once they get their first box",
        linkText: 'Copy link again',
      },
      analytics: {
        category: 'funnel',
        action: 'click_order_confirmation_raf_banner',
        properties: {
          banner_variant: 'stepper',
          order_confirmation_type: 'signup',
          credit_formatted: '£20',
          tiers_claimed: 1,
          tiers_total: 3,
        },
      },
    },
  },
}

export const SUBSCRIPTION_SETTINGS_OVERVIEW_FIXTURE: SubscriptionSettingsOverviewData = {
  sections: [
    {
      type: 'subscription_status_hero',
      data: {
        headline: "Emily, your subscription's currently cancelled",
        subtitle: "Here's how to get back on the menu",
      },
    },
    {
      type: 'next_box_offer',
      data: {
        badge: '25% off',
        label: '25% off your next box',
        description: 'Whether you resubscribe or order a one-off box',
        expiry_date: '2026-01-05',
        expiry_label: 'Offer ends 5th January 2026',
      },
    },
    {
      type: 'subscription_options',
      data: {
        options: [
          {
            id: 'resubscribe',
            title: 'Resubscribe',
            summary: 'Save an extra **£2.50** on every box',
            badge: 'Best value',
            tags: ['Skip anytime', 'Delivery slot guaranteed'],
            action: {
              intent: 'reactivate_subscription',
              title: 'Resubscribe • Save £2.50',
              analytics: {
                category: 'funnel',
                action: 'resubscribe',
                properties: { discount_offered_pct: 25, discount_offered_fixed: null },
              },
            },
          },
          {
            id: 'one_off_box',
            title: 'Order a one-off box',
            summary: 'Not ready to subscribe?',
            tags: ['No commitment', 'Next day delivery available'],
            action: {
              intent: 'start_one_off_order',
              title: 'Order a one-off box',
              analytics: {
                category: 'funnel',
                action: 'order_one_off_box',
                properties: { discount_offered_pct: null, discount_offered_fixed: null },
              },
            },
          },
        ],
      },
    },
  ],
}

// The discount expires on the Thursday, so the Friday carries none.
const RESUBSCRIPTION_DAY_OPTIONS: DeliveryDayOption[] = [
  {
    daySlotLeadTimeId: 'dsl-thu-1',
    date: '2026-08-13',
    label: 'Thursdays',
    selectLabel: 'Select Thursdays (starting 13 Aug)',
    discount: {
      badge: '25% off',
      expiry_date: '2026-08-13',
      expiry_label: 'Offer ends Thu 13 Aug',
    },
  },
  {
    daySlotLeadTimeId: 'dsl-fri-1',
    date: '2026-08-14',
    label: 'Fridays',
    selectLabel: 'Select Fridays (starting 14 Aug)',
  },
]

const RESUBSCRIPTION_ADDRESS_OPTIONS = [
  {
    addressId: 'address-home',
    name: 'Home',
    line1: '123 London Road',
    town: 'London',
    postcode: 'AB12 3CD',
    label: '123 London Road, London, AB12 3CD',
    deliveryDayOptions: RESUBSCRIPTION_DAY_OPTIONS,
    deliveryDayRangeLabel: '13th Aug - 14th Aug',
  },
  {
    addressId: 'address-work',
    name: 'Work',
    line1: '1 Enterprise Way',
    postcode: 'EF34 5GH',
    label: '1 Enterprise Way, EF34 5GH',
    deliveryDayOptions: [RESUBSCRIPTION_DAY_OPTIONS[1]],
    deliveryDayRangeLabel: '14th Aug',
  },
]

const RESUBSCRIPTION_BOX_SIZE_OPTIONS = [
  {
    numPortions: 2,
    label: 'For 2 people',
    title: '2 person box',
    recipesLabel: 'Choose **2 to 5** recipes per box.',
    suitableFor: ['2 adults (or 1 + leftovers)', '1 adult and 1-2 children'],
  },
  {
    numPortions: 4,
    label: 'For 4 people',
    title: '4 person box',
    recipesLabel: 'Choose **2 to 5** recipes per box.',
    suitableFor: ['4 adults (or 2-3 + leftovers)', '2 adults and 2-3 children'],
  },
]

export const SUBSCRIPTION_SETTINGS_RESUBSCRIBE_FIXTURE: SubscriptionSettingsResubscribeData = {
  sections: [
    { type: 'resubscription_hero', data: { headline: "Everything's as you left it" } },
    {
      type: 'resubscription_offer',
      data: {
        next_box_intro: 'Fresh food, back on your table from',
        next_box_date_label: 'Thursday 13 August',
      },
    },
    {
      type: 'settings_confirmation',
      data: {
        deliveryDay: {
          current: RESUBSCRIPTION_DAY_OPTIONS[0],
          heading: 'Delivery day',
          pickerTitle: 'Choose next delivery date',
          editAction: {
            intent: 'edit_resubscribe_delivery_day',
            title: 'Edit',
            analytics: { category: 'funnel', action: 'edit_resubscribe_delivery_day' },
          },
        },
        address: {
          current: RESUBSCRIPTION_ADDRESS_OPTIONS[0],
          options: RESUBSCRIPTION_ADDRESS_OPTIONS,
          heading: 'Delivery address',
          pickerTitle: 'Choose delivery address',
          pickerSubtitle: 'Please select the address you would like us to deliver to',
          editAction: {
            intent: 'edit_resubscribe_address',
            title: 'Edit',
            analytics: { category: 'funnel', action: 'edit_resubscribe_address' },
          },
        },
        boxSize: {
          current: RESUBSCRIPTION_BOX_SIZE_OPTIONS[0],
          options: RESUBSCRIPTION_BOX_SIZE_OPTIONS,
          heading: 'Box size',
          title: 'Change box size',
          suitableForTitle: 'Suitable for:',
          saveLabel: 'Save',
          cancelLabel: 'Cancel',
          editAction: {
            intent: 'edit_resubscribe_box_size',
            title: 'Edit',
            analytics: { category: 'funnel', action: 'edit_resubscribe_box_size' },
          },
        },
      },
    },
    {
      type: 'resubscription_benefits_web',
      data: { benefits: ['Skip anytime', 'Delivery slot guaranteed', 'No commitment'] },
    },
    {
      type: 'resubscription_benefits_mobile',
      data: { benefits: ['Skip anytime'] },
    },
    {
      type: 'resubscription_cta',
      data: {
        title: 'Confirm and see menu',
        analytics: { category: 'funnel', action: 'reactivate_subscription_complete' },
      },
    },
  ],
}

const withoutDiscount = ({
  daySlotLeadTimeId,
  date,
  label,
  selectLabel,
}: DeliveryDayOption): DeliveryDayOption => ({ daySlotLeadTimeId, date, label, selectLabel })

export const SUBSCRIPTION_SETTINGS_RESUBSCRIBE_NO_DISCOUNT_FIXTURE: SubscriptionSettingsResubscribeData =
  {
    sections: SUBSCRIPTION_SETTINGS_RESUBSCRIBE_FIXTURE.sections.map((section) =>
      section.type === 'settings_confirmation'
        ? {
            type: 'settings_confirmation' as const,
            data: {
              ...section.data,
              deliveryDay: {
                ...section.data.deliveryDay,
                current: withoutDiscount(section.data.deliveryDay.current),
              },
              address: {
                ...section.data.address,
                options: section.data.address.options.map((address) => ({
                  ...address,
                  deliveryDayOptions: address.deliveryDayOptions.map(withoutDiscount),
                })),
              },
            },
          }
        : section,
    ),
  }

export const ORDER_CONFIRMATION_RAF_COMPACT_FIXTURE: OrderConfirmationBanner = {
  type: 'raf_stepper',
  data: {
    title: "Know someone who'd love these recipes?",
    description: 'Earn £20 credit for each friend you bring to Gousto',
    creditFormatted: '£20',
    footerText: 'They get 65% off first, then 45% and 25% for 2 months.',
    analytics: {
      category: 'funnel',
      action: 'order_confirmation_raf_banner_displayed',
      properties: {
        banner_variant: 'compact',
        order_confirmation_type: 'standard',
        credit_formatted: '£20',
      },
    },
    action: {
      intent: 'refer_a_friend',
      title: 'Share my code',
      webTitle: 'Copy share link',
      successMessage: {
        title: 'Link copied, now send it to a friend',
        subtitle: "You'll get £20 credit once they get their first box",
        linkText: 'Copy link again',
      },
      analytics: {
        category: 'funnel',
        action: 'click_order_confirmation_raf_banner',
        properties: {
          banner_variant: 'compact',
          order_confirmation_type: 'standard',
          credit_formatted: '£20',
        },
      },
    },
  },
}

export const RESUBSCRIPTION_MODAL_FIXTURE: ResubscriptionModalData = {
  headline: "You're back, Jefferson",
  badge: { label: '**25% off** your next box', discountValue: 25 },
  message:
    "Your subscription's **active** again. Your first box arrives **Thursday 19 August** - choose recipes for your first box now.",
  primaryAction: {
    intent: 'dismiss_resubscription_modal',
    title: 'Choose recipes',
    analytics: { category: 'funnel', action: 'resubscription_modal_choose_recipes' },
  },
  secondaryAction: {
    intent: 'navigate_to_dashboard',
    title: "I'll pick later",
    analytics: { category: 'funnel', action: 'resubscription_modal_pick_later' },
  },
  dismissAnalytics: {
    category: 'funnel',
    action: 'resubscription_modal_close',
    properties: { source: 'resubscription_modal' },
  },
}

export const RESUBSCRIPTION_MODAL_NO_BADGE_FIXTURE: ResubscriptionModalData = {
  headline: RESUBSCRIPTION_MODAL_FIXTURE.headline,
  message: RESUBSCRIPTION_MODAL_FIXTURE.message,
  primaryAction: RESUBSCRIPTION_MODAL_FIXTURE.primaryAction,
  secondaryAction: RESUBSCRIPTION_MODAL_FIXTURE.secondaryAction,
  dismissAnalytics: RESUBSCRIPTION_MODAL_FIXTURE.dismissAnalytics,
}
